import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../index';

export type PaperEditorMode = 'hidden' | 'modal' | 'fullscreen';
export type SidebarMode = 'annotate' | 'evaluate';

interface ViewState {
  // Base view mode (reading or canvas)
  baseView: 'reading' | 'canvas';

  // Paper editor state
  paperEditorMode: PaperEditorMode;
  paperEditorPosition?: { x: number; y: number };
  paperEditorSize?: { width: number; height: number };

  // Resource Management Modal state
  resourceManagementModalOpen: boolean;
  resourceManagementModalTab: 'readings' | 'canvases' | 'papers';

  // Active org context (Phase 3). null = Personal namespace.
  // Persisted with viewSlice so refresh preserves the user's context.
  selectedOrgId: string | null;

  isDemoMode: boolean;

  // Sidebar's Annotate / Evaluate toggle. Also gates the inactive
  // "Grade contributor" picker on the reader/canvas.
  sidebarMode: SidebarMode;
}

const initialState: ViewState = {
  baseView: 'reading',
  paperEditorMode: 'hidden',
  resourceManagementModalOpen: false,
  resourceManagementModalTab: 'readings',
  selectedOrgId: null,
  isDemoMode: false,
  sidebarMode: 'annotate',
};

const viewSlice = createSlice({
  name: 'view',
  initialState,
  reducers: {
    setBaseView: (state, action: PayloadAction<'reading' | 'canvas'>) => {
      state.baseView = action.payload;
    },

    setPaperEditorMode: (state, action: PayloadAction<PaperEditorMode>) => {
      state.paperEditorMode = action.payload;
    },

    setPaperEditorPosition: (state, action: PayloadAction<{ x: number; y: number }>) => {
      state.paperEditorPosition = action.payload;
    },

    setPaperEditorSize: (state, action: PayloadAction<{ width: number; height: number }>) => {
      state.paperEditorSize = action.payload;
    },

    resetViewState: (state) => {
      state.baseView = 'reading';
      state.paperEditorMode = 'hidden';
    },

    openResourceManagementModal: (
      state,
      action: PayloadAction<'readings' | 'canvases' | 'papers'>
    ) => {
      state.resourceManagementModalOpen = true;
      state.resourceManagementModalTab = action.payload;
    },

    closeResourceManagementModal: (state) => {
      state.resourceManagementModalOpen = false;
    },

    setSelectedOrgId: (state, action: PayloadAction<string | null>) => {
      state.selectedOrgId = action.payload;
    },

    setIsDemoMode: (state, action: PayloadAction<boolean>) => {
      state.isDemoMode = action.payload;
    },

    setSidebarMode: (state, action: PayloadAction<SidebarMode>) => {
      state.sidebarMode = action.payload;
    },
  },
});

export const {
  setBaseView,
  setPaperEditorMode,
  setPaperEditorPosition,
  setPaperEditorSize,
  resetViewState,
  openResourceManagementModal,
  closeResourceManagementModal,
  setSelectedOrgId,
  setIsDemoMode,
  setSidebarMode,
} = viewSlice.actions;

// Selectors
export const selectBaseView = (state: RootState) => state.view.baseView;
export const selectPaperEditorMode = (state: RootState) => state.view.paperEditorMode;
export const selectPaperEditorPosition = (state: RootState) => state.view.paperEditorPosition;
export const selectPaperEditorSize = (state: RootState) => state.view.paperEditorSize;
export const selectResourceManagementModalOpen = (state: RootState) =>
  state.view.resourceManagementModalOpen;
export const selectResourceManagementModalTab = (state: RootState) =>
  state.view.resourceManagementModalTab;
export const selectSelectedOrgId = (state: RootState) => state.view.selectedOrgId;
export const selectIsDemoMode = (state: RootState) => state.view.isDemoMode;
export const selectSidebarMode = (state: RootState) => state.view.sidebarMode;

export default viewSlice.reducer;
